import { useState } from 'react';
import { LogIn, UserPlus, Loader2, PenLine, CalendarDays, ListChecks, Sparkles } from 'lucide-react';
import { useAuthStore } from '../store/authStore';

// Handgezeichnete Deko-Marks (currentColor → über text-* einfärbbar)
const Underline = ({ className = '' }: { className?: string }) => (
  <svg viewBox="0 0 200 16" fill="none" className={className} aria-hidden>
    <path d="M4 9 C 30 4, 60 13, 95 8 S 165 4, 196 11" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);
const Star = ({ className = '' }: { className?: string }) => (
  <svg viewBox="0 0 40 40" fill="none" className={className} aria-hidden>
    <path d="M20 4 L 24 15 L 36 16 L 26 23 L 30 35 L 20 28 L 10 35 L 14 23 L 4 16 L 16 15 Z" stroke="currentColor" strokeWidth="2.5" strokeLinejoin="round" />
  </svg>
);
const Arrow = ({ className = '' }: { className?: string }) => (
  <svg viewBox="0 0 100 40" fill="none" className={className} aria-hidden>
    <path d="M6 22 C 30 18, 60 14, 86 20" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M78 12 L 90 20 L 80 28" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const FEATURES = [
  { icon: PenLine, label: 'Arbeitsblätter professionell gestalten' },
  { icon: Sparkles, label: 'Inhalte per KI-Assistent erzeugen' },
  { icon: CalendarDays, label: 'Kalender & Stundenplan im Blick' },
  { icon: ListChecks, label: 'Aufgaben & Notizen organisieren' },
];

export default function AuthView() {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const { login, register, isLoading } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      if (mode === 'login') await login(email, password);
      else await register(name, email, password);
    } catch (err: any) {
      setError(err.message || 'Ein Fehler ist aufgetreten');
    }
  };

  return (
    <div className="min-h-screen flex bg-paper">
      {/* ── Markenseite (Desktop) ─────────────────────────── */}
      <div className="hidden lg:flex lg:w-[46%] relative overflow-hidden sl-dotgrid"
        style={{ background: 'linear-gradient(150deg, #F8EBC7 0%, #F6F1E6 45%, #E1E4F5 100%)' }}>
        {/* Deko-Marks */}
        <Star className="absolute top-[14%] right-[18%] w-10 h-10 text-coral rotate-12" />
        <Star className="absolute bottom-[26%] left-[12%] w-6 h-6 text-sun-deep -rotate-6" />
        <Arrow className="absolute top-[42%] right-[12%] w-24 h-10 text-sage -rotate-6" />

        <div className="relative z-10 flex flex-col justify-center px-14 xl:px-20 w-full">
          <img src="/brand/wordmark.svg" alt="SketchLessons" className="h-11 mb-12 self-start" />

          <h1 className="font-serif text-[2.8rem] xl:text-5xl leading-[1.1] text-ink mb-5">
            Arbeitsblätter,
            <br />
            die{' '}
            <span className="relative inline-block">
              <span className="text-cobalt">begeistern</span>
              <Underline className="absolute -bottom-2 left-0 w-full h-3 text-coral" />
            </span>
            .
          </h1>
          <p className="text-ink-soft text-lg leading-relaxed max-w-md mb-10">
            Gestalte didaktisch saubere Materialien für die Grundschule und weiterführende
            Klassen — modern, schnell und direkt als PDF.
          </p>

          <ul className="space-y-3.5">
            {FEATURES.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-3 text-ink-soft">
                <span className="w-9 h-9 rounded-xl bg-white/70 border border-rule flex items-center justify-center flex-shrink-0">
                  <Icon className="w-[18px] h-[18px] text-cobalt" />
                </span>
                <span className="font-medium">{label}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* ── Formular ──────────────────────────────────────── */}
      <div className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">
          {/* Logo (mobil zentriert, Desktop dezent) */}
          <div className="flex flex-col items-center lg:items-start mb-8">
            <img src="/brand/wordmark.svg" alt="SketchLessons" className="h-9 mb-5 lg:hidden" />
            <img src="/brand/mark.svg" alt="" className="hidden lg:block w-12 h-12 mb-4" />
            <h2 className="sl-h2 text-center lg:text-left">
              {mode === 'login' ? 'Willkommen zurück' : 'Konto erstellen'}
            </h2>
            <p className="text-ink-mute mt-1 text-center lg:text-left">
              {mode === 'login'
                ? 'Melde dich an, um weiterzuarbeiten.'
                : 'Starte in wenigen Sekunden los.'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <div>
                <label className="block text-sm font-medium text-ink-soft mb-1.5">Name</label>
                <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                  className="sl-input" placeholder="Dein Name" required />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1.5">E-Mail</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                className="sl-input" placeholder="name@schule.de" required />
            </div>

            <div>
              <label className="block text-sm font-medium text-ink-soft mb-1.5">Passwort</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="sl-input" placeholder="••••••••" required minLength={6} />
            </div>

            {error && (
              <div className="text-coral text-sm font-medium bg-coral-soft border border-coral/20 rounded-xl px-3 py-2">
                {error}
              </div>
            )}

            <button type="submit" disabled={isLoading} className="sl-button sl-button-primary w-full !justify-center">
              {isLoading ? <Loader2 className="w-5 h-5 animate-spin" />
                : mode === 'login' ? <><LogIn className="w-5 h-5" />Anmelden</>
                : <><UserPlus className="w-5 h-5" />Konto erstellen</>}
            </button>
          </form>

          <div className="mt-6 text-center text-sm text-ink-mute">
            {mode === 'login' ? 'Noch kein Konto?' : 'Bereits registriert?'}{' '}
            <button
              onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}
              className="font-semibold text-cobalt hover:text-cobalt-deep transition-colors"
            >
              {mode === 'login' ? 'Jetzt registrieren' : 'Hier anmelden'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
